import React, { useState, useEffect, useCallback } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Stack,
  Chip,
  Divider,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from "@mui/material";
import {
  Close as CloseIcon,
  Info as InfoIcon,
  TaskAlt as TaskAltIcon,
  Edit as EditIcon,
  History as HistoryIcon
} from "@mui/icons-material";
import useDrawerResize from "./useDrawerResize";
import DrawerResizeHandle from "./DrawerResizeHandle";
import { getRoleDetail, getRoleResponsibilitiesHistory } from "../api/coordinatorApi";
import { notification } from "../utils/messages";

const DRAWER_WIDTH = 480;
const DRAWER_MIN_WIDTH = 360;
const DRAWER_MAX_WIDTH = 960;

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export default function RoleDetailDrawer({ open, onClose, roleData, postTypes, onEdit }) {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Role definitions and their history are maintained by LVB India only.
  // u_type 0 = LVB India admin, 1 = city admin, 2 = city user.
  const loginUser = JSON.parse(localStorage.getItem("userData") || "{}");
  const isAdmin = loginUser?.u_type === 0;
  const canEditRole = isAdmin;
  const canViewHistory = isAdmin;

  const { width: drawerWidth, onDragStart: onResizeStart } = useDrawerResize(
    "roleDetail",
    DRAWER_WIDTH,
    DRAWER_MIN_WIDTH,
    DRAWER_MAX_WIDTH
  );

  useEffect(() => {
    if (open && roleData?.id) {
      loadDetail(roleData.id);
      setActiveTab(0);
      setHistoryData([]);
    }
  }, [open, roleData]);

  const loadDetail = async (id) => {
    setLoading(true);
    try {
      const response = await getRoleDetail(id);
      if (response.status === 1) {
        setDetail(response.output);
      }
    } catch (error) {
      notification({ type: "error", message: "Failed to load role details" })();
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = useCallback(async (roleId) => {
    setHistoryLoading(true);
    try {
      const response = await getRoleResponsibilitiesHistory(roleId);
      if (response.status === 1) {
        setHistoryData(response.output || []);
      }
    } catch (error) {
      notification({ type: "error", message: "Failed to load history" })();
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const handleTabChange = (_event, newValue) => {
    if (newValue === 2 && !canViewHistory) return;
    setActiveTab(newValue);
    if (newValue === 2 && detail?.id && historyData.length === 0) {
      loadHistory(detail.id);
    }
  };

  const getPostTypeName = (postTypeId) => {
    const pt = postTypes?.find((p) => p.id === postTypeId);
    return pt?.name || "-";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: drawerWidth,
          bgcolor: "background.paper",
          boxShadow: "-10px 0 30px rgba(0, 0, 0, 0.05)"
        }
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", position: "relative" }}>
        <DrawerResizeHandle onMouseDown={onResizeStart} />
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary" }}>
              Role Details
            </Typography>
          </Stack>
        </Box>

        {/* Content */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
            <CircularProgress />
          </Box>
        ) : detail ? (
          <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
            {/* Header Card */}
            <Box sx={{ px: 3, pt: 3 }}>
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "grey.50",
                  boxShadow: "sm"
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>
                      POST TYPE
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "text.primary", mt: 0.25 }}>
                      {detail.post_type_name || getPostTypeName(detail.post_type_id)}{" "}
                      {detail.is_self === 1 && (
                        <Chip
                          label="Self"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.65rem",
                            fontWeight: 700,
                            bgcolor: "info.lighter",
                            color: "info.dark",
                            verticalAlign: "middle"
                          }}
                        />
                      )}
                    </Typography>
                  </Box>
                  <Chip
                    label={detail.is_active === 1 ? "Active" : "Inactive"}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.7rem",
                      bgcolor: detail.is_active === 1 ? "success.light" : "grey.200",
                      color: detail.is_active === 1 ? "success.dark" : "text.secondary"
                    }}
                  />
                </Stack>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>
                  ROLE
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: "primary.main", mt: 0.25 }}>
                  {detail.name}
                </Typography>
                {detail.sort_order != null && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>
                      POSITION
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary", mt: 0.25 }}>
                      {detail.sort_order}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3, mt: 2 }}>
              <Tabs value={activeTab} onChange={handleTabChange}>
                <Tab label="Overview" sx={{ minHeight: 40, py: 0, textTransform: "none", fontWeight: 600, fontSize: "0.875rem" }} />
                <Tab label="Responsibilities" sx={{ minHeight: 40, py: 0, textTransform: "none", fontWeight: 600, fontSize: "0.875rem" }} />
                {canViewHistory && (
                  <Tab label="History" icon={<HistoryIcon sx={{ fontSize: 16 }} />} iconPosition="start" sx={{ minHeight: 40, py: 0, textTransform: "none", fontWeight: 600, fontSize: "0.875rem" }} />
                )}
              </Tabs>
            </Box>

            {/* Tab Panels */}
            <Box sx={{ flex: 1, overflowY: "auto", px: 3 }}>
              {/* Overview Tab */}
              <TabPanel value={activeTab} index={0}>
                <Box sx={{ mb: 3 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <InfoIcon sx={{ color: "primary.main", fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Role Overview
                    </Typography>
                  </Stack>
                  <Box sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1, bgcolor: "background.paper" }}>
                    <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.7 }}>
                      {detail.role_overview || "No overview provided."}
                    </Typography>
                  </Box>
                </Box>

                {detail.footer_note && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "text.secondary" }}>
                      Footer Note
                    </Typography>
                    <Box sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1, bgcolor: "grey.50" }}>
                      <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                        {detail.footer_note}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </TabPanel>

              {/* Responsibilities Tab */}
              <TabPanel value={activeTab} index={1}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <TaskAltIcon sx={{ color: "primary.main", fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Key Responsibilities
                    </Typography>
                  </Stack>
                  <Chip
                    label={`${detail.responsibilities?.length || 0} Total`}
                    size="small"
                    sx={{ bgcolor: "grey.100", fontWeight: 500 }}
                  />
                </Stack>

                <Stack spacing={2}>
                  {detail.responsibilities?.map((resp, index) => (
                    <Stack key={resp.id || index} direction="row" spacing={2}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          bgcolor: index === 0 ? "primary.main" : "grey.200",
                          color: index === 0 ? "white" : "text.secondary",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 600,
                          fontSize: "0.875rem",
                          flexShrink: 0
                        }}
                      >
                        {String(index + 1).padStart(2, "0")}
                      </Box>
                      <Box sx={{ flex: 1, pb: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                        {resp.title ? (
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary", mb: 0.5 }}>
                            {resp.title}
                          </Typography>
                        ) : null}
                        <Typography variant="body2" sx={{ color: resp.title ? "text.secondary" : "text.primary", fontSize: "0.875rem" }}>
                          {resp.description || "No description provided."}
                        </Typography>
                      </Box>
                    </Stack>
                  ))}
                  {(!detail.responsibilities || detail.responsibilities.length === 0) && (
                    <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                      No responsibilities defined.
                    </Typography>
                  )}
                </Stack>
              </TabPanel>

              {/* History Tab */}
              {canViewHistory && (
              <TabPanel value={activeTab} index={2}>
                {historyLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : historyData.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <HistoryIcon sx={{ fontSize: 40, color: "grey.300", mb: 1 }} />
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      No history yet.
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: "none" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Updated By</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Forced Re-accept</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {historyData.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell sx={{ fontSize: "0.8rem" }}>{formatDate(row.created_at)}</TableCell>
                            <TableCell sx={{ fontSize: "0.8rem" }}>{row.admin_name || "-"}</TableCell>
                            <TableCell sx={{ fontSize: "0.8rem" }}>
                              <Chip
                                label={row.force_re_accept ? "Yes" : "No"}
                                size="small"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "0.7rem",
                                  bgcolor: row.force_re_accept ? "warning.light" : "grey.100",
                                  color: row.force_re_accept ? "warning.dark" : "text.secondary"
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </TabPanel>
              )}
            </Box>
          </Box>
        ) : null}

        {/* Footer */}
        {onEdit && canEditRole && (
          <Box
            sx={{
              px: 3,
              py: 2,
              borderTop: "1px solid",
              borderColor: "divider"
            }}
          >
            <Button
              fullWidth
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => {
                onClose();
                onEdit(detail);
              }}
              sx={{ py: 1.25, fontWeight: 600 }}
            >
              Edit Role
            </Button>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
